import {
  ASTKindToNode,
  DocumentNode,
  FieldNode,
  GraphQLSchema,
  OperationDefinitionNode,
  SelectionSetNode,
  TypeInfo,
  Visitor,
  getVisitFn,
  isAbstractType,
  isInterfaceType,
  parse,
  visit,
  visitWithTypeInfo,
} from 'graphql';
import { merge } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React from 'react';

import { useQueryPlan } from 'src/app/graph/explorerPage/helpers/federationHelpers';
import { useActiveDefinitionDetails } from 'src/app/graph/explorerPage/hooks/useActiveDefinitionDetails';
import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { getLastFromMinLengthArray } from 'src/lib/getLastFromMinLengthArray';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';

import { SafePlanNode } from '../../../helpers/queryPlanHelpers';
import { EditorHint } from '../editorHint/EditorHint';
import { visitThroughFragments } from '../hintsHelpers';

function combineSubgraphTrees(...trees: SubgraphTree[]): SubgraphTree {
  return merge({}, ...trees);
}

/**
 * Visit an ast while tracking the subgraph each field comes from.
 */
function visitWithSubgraphTree(
  subgraphTreeStack: MinLengthArray<1, SubgraphTree>,
  schema: GraphQLSchema,
  typeInfo: TypeInfo,
  visitor: Visitor<ASTKindToNode>,
): Visitor<ASTKindToNode> {
  return {
    enter(node, ...rest) {
      const subgraphTree = getLastFromMinLengthArray(subgraphTreeStack);
      switch (node.kind) {
        case 'Field': {
          const nodeName = node.name.value;
          const type = typeInfo.getParentType();

          // Look in the current type, as well as any union/interfaces matching
          // the current type. The tree might not match the operation exactly,
          // and might be missing some fragment guards, or have extra.
          const possibleTypes = [
            ...(type ? [type] : []),
            ...(isAbstractType(type) ? schema.getPossibleTypes(type) : []),
            ...(isInterfaceType(type)
              ? schema.getImplementations(type).interfaces
              : []),
          ];
          const possibleContainers = [
            subgraphTree,
            ...possibleTypes.flatMap((possibleType) => {
              const typeName = possibleType.name;
              if (subgraphTree.fields && typeName in subgraphTree.fields) {
                return subgraphTree.fields[typeName];
              }
              return [];
            }),
          ];

          const childTrees = possibleContainers.flatMap((possibleContainer) => {
            if (
              possibleContainer?.fields &&
              nodeName in possibleContainer.fields
            ) {
              return possibleContainer.fields[nodeName] ?? [];
            }
            return [];
          });

          if (!childTrees.length) {
            return false;
          }
          subgraphTreeStack.push(combineSubgraphTrees(...childTrees));
          break;
        }
        case 'InlineFragment':
        case 'FragmentDefinition': {
          const condition = node.typeCondition?.name.value;
          // If we find a match to the condition, include that tree as well
          const fieldCondition = condition && subgraphTree.fields?.[condition];
          if (fieldCondition) {
            subgraphTreeStack.push(
              combineSubgraphTrees(subgraphTree, fieldCondition),
            );
          } else {
            subgraphTreeStack.push(subgraphTree);
          }
          break;
        }
        default: {
          break;
        }
      }
      const fn = getVisitFn(visitor, node.kind, false);
      return fn?.apply(visitor, [node, ...rest]);
    },
    leave(node, ...rest) {
      const fn = getVisitFn(visitor, node.kind, true);
      const result = fn?.apply(visitor, [node, ...rest]);
      switch (node.kind) {
        case 'Field':
        case 'InlineFragment':
        case 'FragmentDefinition': {
          subgraphTreeStack.pop();
          break;
        }
        default: {
          break;
        }
      }
      return result;
    },
  };
}

function addToSubgraphHints(
  subgraphTree: SubgraphTree,
  document: DocumentNode,
  schema: GraphQLSchema,
) {
  const hints: Record<number, string[]> = {};
  const operation = document.definitions.find(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === 'OperationDefinition',
  );

  if (!operation) {
    return [];
  }

  const typeInfo = new TypeInfo(schema);
  const subgraphTreeStack: MinLengthArray<1, SubgraphTree> = [subgraphTree];

  visitThroughFragments(
    document,
    visitWithTypeInfo(
      typeInfo,
      visitWithSubgraphTree(subgraphTreeStack, schema, typeInfo, {
        Field: {
          enter(node) {
            const { subgraph } = getLastFromMinLengthArray(subgraphTreeStack);
            if (subgraph && node.loc) {
              const lineNumber = node.loc.startToken.line;
              getOrSetRecord(lineNumber, [], hints).push(subgraph);
            }
          },
        },
      }),
    ),
  );

  return hints;
}

function getPathsInNode(startingPoint: SelectionSetNode | FieldNode) {
  const currentPath: string[] = [];
  const fields: string[][] = [];
  visit(startingPoint, {
    Field: {
      enter(subNode) {
        currentPath.push(subNode.name.value);
        fields.push([...currentPath]);
      },
      leave() {
        currentPath.pop();
      },
    },
    InlineFragment: {
      enter(subNode) {
        if (subNode.typeCondition) {
          currentPath.push(subNode.typeCondition.name.value);
        }
      },
      leave(subNode) {
        if (subNode.typeCondition) {
          currentPath.pop();
        }
      },
    },
  });
  return fields;
}

type SubgraphTree = {
  subgraph?: string;
  fields?: Record<string, SubgraphTree>;
};
// Transform a flat list of fields paths to their subgraphs into a tree form
function getSubgraphTree(
  subgraphNameByField: { path: string[]; subgraph: string }[],
): SubgraphTree {
  const result: SubgraphTree = {};
  subgraphNameByField.forEach(({ path, subgraph }) => {
    let currentObject = result;
    path.forEach((segment, i) => {
      const isLastSegment = i === path.length - 1;
      const currentFields = (currentObject.fields = currentObject.fields ?? {});
      currentObject = currentFields[segment] = currentFields[segment] ?? {};
      if (isLastSegment) {
        currentObject.subgraph = subgraph;
      }
    });
  });
  return result;
}

function getSubgraphNameByField(
  node: SafePlanNode,
  path: string[] = [],
): { path: string[]; subgraph: string }[] {
  switch (node.kind) {
    case 'Subscription': {
      return getSubgraphNameByField(node.primary, path);
    }
    case 'Fetch': {
      const definitions =
        // TODO: is this safe? operation was not included in the safe types.
        // once we confirm if it is or not, we can add the `operation` type to the Fetch node type
        'operation' in node && typeof node.operation === 'string'
          ? parse(node.operation).definitions
          : [];
      return definitions.flatMap((definition) => {
        if (definition.kind !== 'OperationDefinition') {
          return [];
        }
        return definition.selectionSet.selections.flatMap((selection) => {
          if (selection.kind !== 'Field') {
            return [];
          }
          const startingPoint =
            selection.name.value === '_entities'
              ? selection.selectionSet
              : selection;
          if (!startingPoint) {
            return [];
          }
          return getPathsInNode(startingPoint).map((subPath) => ({
            path: [...path, ...subPath],
            subgraph: node.serviceName,
          }));
        });
      });
    }
    case 'Flatten': {
      return getSubgraphNameByField(
        node.node,
        node.path.filter(
          (segment): segment is string =>
            typeof segment === 'string' && segment !== '@',
        ),
      );
    }
    case 'Parallel':
    case 'Sequence': {
      return node.nodes.flatMap((childNode) =>
        getSubgraphNameByField(childNode, path),
      );
    }
    case 'Defer': {
      return [node.primary, ...node.deferred].flatMap(({ node: childNode }) => {
        if (!childNode) return [];
        return getSubgraphNameByField(childNode, path);
      });
    }
    case 'Condition': {
      if (!node.elseClause && !node.ifClause) {
        return assertUnreachableOrReturnDefault(node, []);
      }
      return getSubgraphNameByField(node.elseClause ?? node.ifClause, path);
    }
    default: {
      return assertUnreachableOrReturnDefault(node, []);
    }
  }
}

/**
 * Attaches the subgraph for each leaf field as a hint on the right.
 */
export const SubgraphHints = ({
  editor,
  schema,
  hasSupergraphSchema,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  schema: GraphQLSchema;
  hasSupergraphSchema: boolean;
}) => {
  const activeDefinitionDetails = useActiveDefinitionDetails(editor, schema);
  const activeDocument = activeDefinitionDetails?.activePartsOfDocument;
  const activePartsOfDocument = activeDefinitionDetails?.isValid
    ? activeDefinitionDetails.activePartsOfDocument
    : undefined;
  const documentLength = activePartsOfDocument?.split('\n')?.length ?? 0;

  const graphRef = useGraphRef();
  const result = useQueryPlan({
    graphRef,
    document: activeDocument ?? '',
    hasSupergraphSchema,
  });
  const lastResultRef = React.useRef<typeof result>(result);
  if (result.state === 'success' || lastResultRef.current.state !== 'success') {
    lastResultRef.current = result;
  }
  const resultToRender = lastResultRef.current;

  const subgraphTree = React.useMemo(
    () =>
      resultToRender.state === 'success' && resultToRender.object.node
        ? getSubgraphTree(getSubgraphNameByField(resultToRender.object.node))
        : null,
    [resultToRender],
  );

  const hints = React.useMemo(
    () =>
      subgraphTree && activePartsOfDocument
        ? addToSubgraphHints(subgraphTree, parse(activePartsOfDocument), schema)
        : {},
    [subgraphTree, activePartsOfDocument, schema],
  );

  return (
    <>
      {Object.entries(hints).map(([lineNumber, hint]) => (
        <EditorHint
          key={`${documentLength}-${lineNumber}-${hint}`}
          editor={editor}
          lineNumber={parseInt(lineNumber, 10)}
          hint={hint.join(', ')}
        />
      ))}
    </>
  );
};
